import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { getProductBySlug } from '../src/data/products';

const ProductDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const product = slug ? getProductBySlug(slug) : undefined;

  const serialNumber = React.useMemo(() => 
    // eslint-disable-next-line react-hooks/purity
    Math.floor(Math.random() * 9000) + 1000, 
    []
  );

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!product) {
    return <Navigate to="/products" replace />;
  }

  const ETSY_STORE_URL = 'https://www.etsy.com/shop/BornInYU';

  // Generate breadcrumb schema markup for SEO
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://borninyu.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Products',
        item: 'https://borninyu.com/products',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: product.name,
        item: `https://borninyu.com/product/${product.slug}`,
      },
    ],
  };

  // Generate product schema markup for SEO
  const productSchema = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: `https://borninyu.com${product.imageUrl}`,
    sku: product.sku,
    brand: {
      '@type': 'Brand',
      name: product.brand,
    },
    offers: {
      '@type': 'Offer',
      url: product.url,
      priceCurrency: product.currency,
      price: product.price,
      priceValidUntil: '2026-12-31',
      availability: `https://schema.org/${product.availability}`,
      seller: {
        '@type': 'Organization',
        name: 'Born in YU',
      },
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: '5.99',
          currency: 'USD',
        },
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: 'US',
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: {
            '@type': 'QuantitativeValue',
            minValue: 1,
            maxValue: 3,
            unitCode: 'DAY',
          },
          transitTime: {
            '@type': 'QuantitativeValue',
            minValue: 5,
            maxValue: 10,
            unitCode: 'DAY',
          },
        },
      },
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        applicableCountry: 'US',
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 30,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/FreeReturn',
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>{product.name} | Born in YU</title>
        <meta name="description" content={product.description} />
        <meta
          name="keywords"
          content={`Yugoslav ${product.classification}, ${product.name}, brutalist fashion, Yugoslav nostalgia, Balkan apparel, ${product.docNo}`}
        />
        <link rel="canonical" href={`https://borninyu.com/product/${product.slug}`} />

        {/* Open Graph */}
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${product.name} | Born in YU`} />
        <meta property="og:description" content={product.description} />
        <meta property="og:image" content={`https://borninyu.com${product.imageUrl}`} />
        <meta property="og:url" content={product.url} />
        <meta property="product:price:amount" content={product.price?.toString()} />
        <meta property="product:price:currency" content={product.currency} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${product.name} | Born in YU`} />
        <meta name="twitter:description" content={product.description} />
        <meta name="twitter:image" content={`https://borninyu.com${product.imageUrl}`} />

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        {/* Product Schema */}
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      </Helmet>

      <main className="min-h-screen bg-[#F0F0F0]">
        {/* Breadcrumbs */}
        <div className="bg-black text-white p-4">
          <div className="max-w-6xl mx-auto flex items-center gap-2 text-xs font-typewriter uppercase">
            <Link to="/" className="hover:text-[#C41E3A] transition-colors">
              Home
            </Link>
            <span>/</span>
            <Link to="/products" className="hover:text-[#C41E3A] transition-colors">
              Products
            </Link>
            <span>/</span>
            <span className="text-gray-400">{product.docNo}</span>
          </div>
        </div>

        <section className="p-6 md:p-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              {/* Product Image */}
              <div className="border-[3px] border-black bg-white shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]">
                <div className="p-3 border-b-[3px] border-black bg-gray-100 flex justify-between items-center">
                  <div className="text-[10px] font-black uppercase">
                    {t('products.documentId')} {product.id}
                  </div>
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-black"></div>
                    <div className="w-2 h-2 rounded-full bg-red-600 animate-pulse"></div>
                  </div>
                </div>
                <div className="aspect-[4/5] overflow-hidden grainy-overlay bg-gray-100">
                  <img
                    src={product.imageUrl}
                    alt={product.imageAlt || product.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="h-6 border-t-[3px] border-black flex overflow-hidden">
                  {[...Array(24)].map((_, i) => (
                    <div
                      key={i}
                      className="flex-none w-full border-r border-black last:border-r-0 bg-gray-50 odd:bg-gray-200"
                    ></div>
                  ))}
                </div>
              </div>

              {/* Product Details */}
              <div className="border-[3px] border-black bg-white p-8 md:p-12 shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]">
                <div className="mb-6">
                  <div className="text-[10px] font-black text-gray-400 mb-2 font-typewriter">
                    {t('products.docNo')}
                  </div>
                  <div className="text-2xl font-black tracking-tight uppercase mb-2">
                    {product.docNo}
                  </div>
                </div>

                <h1 className="text-3xl md:text-4xl font-black uppercase tracking-tighter mb-4">
                  {product.name}
                </h1>

                <div className="mb-6">
                  <div className="text-[10px] font-black text-gray-400 mb-2 font-typewriter">
                    {t('products.classification')}
                  </div>
                  <div className="text-sm font-bold uppercase">
                    {t(`products.items.${product.classification}`)}
                  </div>
                </div>

                <p className="text-sm font-typewriter uppercase text-gray-700 leading-relaxed mb-8">
                  {product.description}
                </p>

                <div className="mb-8 pb-8 border-b-2 border-gray-200">
                  <div className="text-[10px] font-black text-gray-400 mb-2 font-typewriter">
                    {t('products.issuanceCost')}
                  </div>
                  <div className="text-4xl font-black text-[#C41E3A] mb-4">
                    ${product.price?.toFixed(2)} {product.currency}
                  </div>
                  <div className="inline-block px-3 py-1 border-2 border-black text-xs font-black uppercase bg-white">
                    {product.availability === 'InStock' ? 'In Stock' : 'Out of Stock'}
                  </div>
                </div>

                {/* Features */}
                {product.features && product.features.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-black uppercase mb-4 border-l-4 border-[#C41E3A] pl-3">
                      Features
                    </h3>
                    <ul className="space-y-2">
                      {product.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <span className="text-[#C41E3A] font-black mt-1">✓</span>
                          <span className="font-typewriter uppercase text-xs">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Materials */}
                {product.materials && product.materials.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-black uppercase mb-4 border-l-4 border-[#C41E3A] pl-3">
                      Materials
                    </h3>
                    <ul className="space-y-2">
                      {product.materials.map((material, index) => (
                        <li key={index} className="text-xs font-typewriter uppercase text-gray-700">
                          • {material}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Sizes */}
                {product.sizes && product.sizes.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-black uppercase mb-4 border-l-4 border-[#C41E3A] pl-3">
                      Available Sizes
                    </h3>
                    <div className="flex gap-2 flex-wrap">
                      {product.sizes.map((size) => (
                        <div
                          key={size}
                          className="px-4 py-2 border-2 border-black text-xs font-black uppercase bg-white hover:bg-black hover:text-white transition-colors"
                        >
                          {size}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* CTA Button */}
                <a
                  href={ETSY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center bg-[#C41E3A] border-2 border-transparent hover:border-black text-white py-4 font-black uppercase text-lg tracking-tighter hover:bg-black transition-all active:translate-y-1"
                >
                  {t('products.button')}
                </a>

                <div className="mt-6 text-[8px] font-typewriter text-gray-400 uppercase">
                  {t('products.serialPrefix')} BC-{serialNumber} / SKU: {product.sku}
                </div>
              </div>
            </div>

            {/* Long Description */}
            {product.longDescription && (
              <div className="mt-12 border-[3px] border-black bg-white p-8 md:p-12 shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]">
                <h2 className="text-2xl font-black uppercase tracking-tighter mb-6 border-l-4 border-[#C41E3A] pl-4">
                  About This {product.classification === 'tshirt' ? 'T-Shirt' : product.classification === 'hoodie' ? 'Hoodie' : product.classification === 'poster' ? 'Poster' : product.classification === 'totebag' ? 'Tote Bag' : 'Product'}
                </h2>
                <div className="space-y-6">
                  {/* Archive-style description */}
                  <p className="font-typewriter text-sm uppercase text-gray-700 leading-relaxed">
                    {product.longDescription}
                  </p>

                  {/* SEO-friendly plain language description */}
                  <div className="pt-6 border-t-2 border-gray-200">
                    <h3 className="text-sm font-black uppercase mb-3 text-gray-500">Product Details</h3>
                    <p className="text-sm text-gray-700 leading-relaxed normal-case">
                      {product.classification === 'tshirt' && `This high-quality cotton t-shirt features ${product.name.toLowerCase()} design celebrating Yugoslav heritage and brutalist architecture. Perfect for fans of Yugoslav nostalgia, socialist design, and Balkan culture. Made from premium cotton for comfort and durability.`}
                      {product.classification === 'hoodie' && `Premium quality hoodie featuring ${product.name.toLowerCase()} design inspired by Yugoslav modernism and brutalist aesthetics. Ideal gift for those who appreciate Yugoslav history, socialist architecture, and Balkan heritage. Comfortable cotton blend construction.`}
                      {product.classification === 'poster' && `Museum-quality poster print featuring ${product.name.toLowerCase()}. Printed on premium matte paper with archival ink for long-lasting display. Perfect wall art for fans of Yugoslav architecture, brutalism, and modernist design. Ready to frame.`}
                      {product.classification === 'totebag' && `Durable canvas tote bag featuring ${product.name.toLowerCase()} design. Heavy-duty construction with reinforced handles for everyday use. Great gift for fans of Yugoslav design, brutalist aesthetics, and functional art. Eco-friendly alternative to plastic bags.`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Back to Products */}
            <div className="mt-12 text-center">
              <Link
                to="/products"
                className="inline-block px-8 py-3 border-2 border-black bg-white text-black font-black uppercase text-sm hover:bg-black hover:text-white transition-all"
              >
                ← Back to Catalog
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default ProductDetail;
